import { environment } from 'environments/environment';

/**
 * One entry on the Fleet Learn page: a place to go, with a sentence saying why.
 */
export interface FleetResource {
  /** Stable id, reused as the command palette's command id. */
  id: string;
  title: string;
  /** Registered icon name, e.g. `heroicons_outline:book-open`. */
  icon: string;
  /**
   * What the reader gets, in one sentence.
   *
   * The reason this file exists at all: it is the field the sidebar had no room
   * for. Write what is *behind* the link, not what the link is called again.
   */
  description: string;
  /** Extra search terms for the command palette. Never shown. */
  keywords: string;
  /**
   * A `path` starting with `/` is an absolute Console route; other paths are
   * relative to `/projects/:projectId/fleet`. A `url` leaves the Console.
   * Exactly one of the two.
   */
  path?: string;
  url?: string;
}

export interface FleetResourceSection {
  title: string;
  resources: FleetResource[];
}

/**
 * The material behind Fleet's `Learn` page — tutorials to work through and
 * references to look things up in.
 *
 * ## Why this is a file and not two lists in two components
 *
 * These five entries used to live in `FleetSidebarComponent` and, separately, in
 * the command palette's registry, which keeps its own copy of every destination
 * on purpose (it has no way to discover routes). Adding the Learn page would
 * have made three copies of the same five URLs, one of which —
 * {@link ConsoleEnvironment.fleetApiDocsUrl} — differs per build. A wrong API
 * link on a staging console is exactly the kind of drift nobody notices until a
 * customer follows it.
 *
 * So the palette and the page read this, and the sidebar reads neither: it is
 * down to a single `Learn` entry now.
 *
 * Fleet-only for now, and deliberately so. Voice, Rooms and Cortex have one
 * documentation link each, and a whole page for one link is a click for
 * nothing. This file sits in `core/fleet/` rather than under `modules/admin/
 * fleet/` because the palette lives in `core/` and must import it — the other
 * direction would be a layering inversion.
 */
export const FLEET_RESOURCE_SECTIONS: FleetResourceSection[] = [
  {
    title: 'Tutorials',
    resources: [
      {
        id: 'fleet.onboarding.minecraft',
        title: 'Minecraft',
        icon: 'heroicons_outline:play-circle',
        description:
          'Go from an empty project to a running server: build an image, write a config, deploy it.',
        keywords: 'fleet onboarding wizard getting started walkthrough',
        path: 'tutorials/minecraft',
      },
      {
        id: 'fleet.onboarding.autoscaling',
        title: 'Autoscaling',
        icon: 'heroicons_outline:arrows-pointing-out',
        description:
          'Build a deployment that scales itself and watch what buffer, minimum and maximum actually do.',
        keywords:
          'fleet autoscaling scaling buffer minimum maximum walkthrough',
        path: 'tutorials/autoscaling',
      },
    ],
  },
  {
    title: 'Documentation',
    resources: [
      {
        id: 'fleet.docs.developer',
        title: 'Developer Guide',
        icon: 'heroicons_outline:book-open',
        description:
          'Concepts and how-tos across images, server configs, deployments and backups.',
        keywords: 'fleet documentation docs help manual',
        url: 'https://docs.4players.io/fleet',
      },
      {
        id: 'fleet.docs.api',
        title: 'API Reference',
        icon: 'heroicons_outline:document-text',
        description:
          'The complete REST surface, generated from this environment’s OpenAPI spec.',
        keywords: 'fleet documentation docs openapi swagger reference rest',
        // Per build: staging Fleet documents a different API than production.
        url: environment.fleetApiDocsUrl,
      },
      {
        id: 'fleet.docs.cli',
        title: 'CLI Reference',
        icon: 'heroicons_outline:command-line',
        description:
          'Every odin command, for scripting deployments and wiring Fleet into CI.',
        keywords: 'fleet documentation docs terminal odin cli command line',
        url: 'https://docs.4players.io/fleet/cli/',
      },
      {
        id: 'fleet.docs.mcp',
        title: 'MCP',
        icon: 'heroicons_outline:link',
        description: 'Set up your AI connection and manage application access.',
        keywords:
          'fleet mcp ai assistant connection integration documentation guide',
        path: '/settings/mcp',
      },
    ],
  },
];
